import useSWR, { mutate } from 'swr';
import { Quiz, QuizAudit, QuizDTO, QuizForm } from '../../models/Quiz';
import { fetcher } from './swr';
import { useCallback } from 'react';
import api from '../api';
import useToast from '../../hooks/useToast';
import { add } from 'date-fns';

const useQuiz = () => {
  const { data, error } = useSWR<Quiz[] | undefined>('/quiz', fetcher, {
    dedupingInterval: 60 * 1000 * 60,
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
  });

  const { addToast } = useToast();

  const toQuiz = (quiz: QuizForm): Partial<QuizDTO> => {
    return {
      quizTitle: quiz.title,
      tags: quiz.tags,
      description: quiz.description,
      questions: quiz.questions.map(question => ({
        description: question.description,
        optionA: question.options[0],
        optionB: question.options[1],
        optionC: question.options[2],
        optionD: question.options[3],
        target: `option${String.fromCharCode(65 + question.correctAnswer)}`,
        tags: question.tags || '',
      })),
    };
  };

  const editQuiz = useCallback(
    async (quizId: string, quiz: Partial<Omit<QuizForm, 'questions'>>) => {
      try {
        await api.put(`/quiz/update`, quiz);
        addToast({
          type: 'success',
          title: 'Questionário atualizado',
          description: 'O questionário foi atualizado com sucesso',
        });
        mutate('/quiz');
      } catch (error) {
        addToast({
          type: 'error',
          title: 'Erro ao atualizar questionário',
          description:
            'Ocorreu um erro ao atualizar o questionário, tente novamente',
        });
      }
    },
    [],
  );

  const classQuizAudit = useCallback(async (classId: string) => {
    const quizzes = await api
      .get<QuizAudit[]>(`/quiz/audit?classId=${classId}`)
      .then(response => response.data);
    return quizzes;
  }, []);

  const attachQuiz = useCallback(
    async (
      file: File,
      quizTitle: string,
      description: string,
      tags?: string,
    ) => {
      const formData = new FormData();
      formData.append('questions', file);
      formData.append('quizTitle', quizTitle);
      formData.append('description', description);
      formData.append('tags', tags || '');

      await api.post('/quiz/attach', formData);
      mutate('/quiz');
    },
    [],
  );

  const getQuizByClassId = useCallback((classId: string) => {
    const quizzes = api
      .get<Quiz[]>(`/quiz/by-class/${classId}`)
      .then(response => response.data);
    return quizzes;
  }, []);

  const removeQuizFromClass = useCallback(
    async (classId: string, quizId: string) => {
      await api.delete(`/quiz/${classId}/${quizId}`);
      mutate('/quiz');
    },
    [],
  );
  const addQuizToClassCourse = useCallback(
    async (classId: string, courseId: string, quizIds: string[]) => {
      await api.post(`/quiz/${classId}/${courseId}/add`, { quizIds });
      mutate('/quiz');
    },
    [],
  );

  const removeQuiz = useCallback(async (id: string) => {
    await api.delete(`/quiz/delete/${id}`);
    mutate('/quiz');
  }, []);

  const addQuiz = useCallback(async (quiz: QuizForm) => {
    const newQuiz = toQuiz(quiz);
    await api.post('/quiz/create', newQuiz);
    mutate('/quiz');
  }, []);

  return {
    error,
    addQuiz,
    editQuiz,
    removeQuiz,
    attachQuiz,
    classQuizAudit,
    getQuizByClassId,
    removeQuizFromClass,
    addQuizToClassCourse,
    quizzes: data || [],
    isLoading: !data && !error,
  };
};

export default useQuiz;
